<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { supabase } from "../lib/supabase.js";

const router = useRouter();
const route = useRoute();

const checkingSession = ref(true);
const sessionReady = ref(false);
const status = ref("Preparing your reset form...");
const errorMessage = ref("");

const password = ref("");
const confirmPassword = ref("");
const submitLoading = ref(false);
const formError = ref("");
const formMessage = ref("");
const success = ref(false);

function parseHashParams(hash) {
  const h = (hash || "").replace(/^#/, "");
  return new URLSearchParams(h);
}

async function ensureRecoverySession() {
  try {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);

    // quick early error check from params if provided by Supabase
    const earlyHashParams = parseHashParams(window.location.hash);
    const errorParam =
      url.searchParams.get("error") || earlyHashParams.get("error");
    const errorDesc =
      url.searchParams.get("error_description") ||
      earlyHashParams.get("error_description");
    if (errorParam || errorDesc) {
      throw new Error(errorDesc || errorParam);
    }

    let authError = null;

    // 1) Preferred: exchange the code for a session
    const code = url.searchParams.get("code");
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(currentUrl);
      authError = error || null;
    } else {
      // 2) Fallback: tokens in the URL hash
      const hashParams = parseHashParams(window.location.hash);
      const access_token = hashParams.get("access_token");
      const refresh_token = hashParams.get("refresh_token");
      if (access_token && refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });
        authError = error || null;
      } else {
        // 3) Final fallback: maybe session is already set
        const { data, error } = await supabase.auth.getSession();
        if (!data?.session) {
          authError = error || new Error("No session found. Please use a valid password reset link.");
        }
      }
    }

    if (authError) throw authError;

    // Optional: clean URL (remove query/hash)
    try {
      await router.replace({ path: route.path, query: {}, hash: "" });
    } catch (_e) {
      // non-blocking
    }

    sessionReady.value = true;
    status.value = "Enter a new password below.";
  } catch (e) {
    console.error("ResetPassword: ensureRecoverySession error:", e);
    errorMessage.value =
      e?.message ||
      "There was a problem preparing your reset form. Please try the link again.";
  } finally {
    checkingSession.value = false;
  }
}

async function submitNewPassword() {
  formError.value = "";
  formMessage.value = "";
  if (!password.value || password.value.length < 8) {
    formError.value = "Password must be at least 8 characters.";
    return;
  }
  if (password.value !== confirmPassword.value) {
    formError.value = "Passwords do not match.";
    return;
  }

  submitLoading.value = true;
  try {
    const { error } = await supabase.auth.updateUser({
      password: password.value,
    });
    if (error) {
      formError.value = error.message || "Failed to update password.";
      return;
    }
    success.value = true;
    formMessage.value = "Your password has been updated successfully.";
  } catch (e) {
    formError.value = e?.message || "Unexpected error updating password.";
  } finally {
    submitLoading.value = false;
  }
}

function goToLogin() {
  router.replace("/login");
}

function continueToApp() {
  router.replace("/home");
}

onMounted(() => {
  ensureRecoverySession();
});
</script>

<template>
  <div class="reset-container">
    <div class="card">
      <h2>Reset Password</h2>

      <template v-if="checkingSession">
        <p>{{ status }}</p>
      </template>

      <template v-else-if="errorMessage">
        <p class="error">{{ errorMessage }}</p>
        <button @click="goToLogin">Back to Login</button>
      </template>

      <template v-else-if="sessionReady">
        <template v-if="!success">
          <p class="subtitle">Enter your new password below.</p>

          <div class="form-group">
            <label for="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              v-model="password"
              placeholder="Enter a new password"
              minlength="8"
              required
              autocomplete="new-password"
            />
          </div>

          <div class="form-group">
            <label for="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              v-model="confirmPassword"
              placeholder="Re-enter your new password"
              minlength="8"
              required
              autocomplete="new-password"
            />
          </div>

          <button :disabled="submitLoading" @click="submitNewPassword">
            {{ submitLoading ? "Updating..." : "Update Password" }}
          </button>

          <p v-if="formError" class="error">{{ formError }}</p>
        </template>

        <template v-else>
          <p class="success">{{ formMessage }}</p>
          <div class="actions">
            <button class="primary" @click="continueToApp">Continue to App</button>
            <button class="secondary" @click="goToLogin">Back to Login</button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.reset-container {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: #f5f5f5;
}

.card {
  background: white;
  padding: 1.75rem 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  max-width: 420px;
  width: 92%;
}

h2 {
  margin: 0 0 0.5rem 0;
}

.subtitle {
  color: #555;
  margin-top: 0;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.45rem;
  color: #333;
}

input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

button {
  width: 100%;
  padding: 0.9rem;
  background-color: #99b5d3;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s ease;
  margin-top: 0.25rem;
}

button:hover {
  background-color: #7d9cbb;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.actions .secondary {
  background: #e6ebf1;
  color: #2f3a48;
}

.actions .secondary:hover {
  background: #d8e0ea;
}

.error {
  color: #d64545;
  margin-top: 0.75rem;
  text-align: center;
}

.success {
  color: #2e7d32;
  margin-top: 0.75rem;
  text-align: center;
}
</style>
